import React, { ReactElement, useEffect, useState } from 'react';
import {
  Col,
  Loader,
  Row,
  usePageNumberFromUrl,
  usePerPageValueFromUrl,
  useSortValueFromUrl,
} from 'idsk-react-components';
import { MobileFilterModal, useResponsiveContext } from 'slovlex-idsk-react-components';
import { useSearchParams } from 'react-router-dom';
import TerminListTable from './component/table/TerminListTable';
import TerminListActions from './component/actionButtons/TerminListActions';
import { TerminListAdminFilter } from '../../../../model/TerminListAdminFilter';
import { SafeParameterSetter } from '../../../../model/SafeParameterSetter';
import FilterTagsComponent from './component/filtertags/FilterTagsComponent';
import SidePanel from './component/sidepanel/SidePanel';
import {
  defaultPerPageValue,
  perPageDataArray,
} from '../../../../constants/PaginationPerPageConstants';
import getUpdatedSearchParams from './function/getUpdatedSearchParams';
import useTerminListPage from './hook/useTerminListPage';
import { Pouzivatel } from '../../../../model/Pouzivatel';
import emptyFilter from './emptyFilter';
import SearchInput from './component/searchInput/SearchInput';
import MobileFilters from './component/sidepanel/MobileFilters';
import countActiveFilters from '../../../../functions/countActiveFilters';

interface Props {
  initialFilter: TerminListAdminFilter
}

const TerminList = ({ initialFilter }: Props): ReactElement => {
  const [firstLoad, setFirstLoad] = useState(true);
  const { page } = usePageNumberFromUrl('page');
  const { perPageValue } = usePerPageValueFromUrl('perPage', perPageDataArray, defaultPerPageValue);
  const [searchParams, setSearchParams] = useSearchParams();
  const { sort } = useSortValueFromUrl();
  const { isSmallScreen } = useResponsiveContext();
  const [filter, setFilter] = useState<TerminListAdminFilter>(initialFilter);
  const [filterCopy, setFilterCopy] = useState<TerminListAdminFilter>(filter);
  const [isOpenModal, setIsOpenModal] = useState(false);

  const onUpdateField: SafeParameterSetter<TerminListAdminFilter> = (fieldName, value): void => {
    setFilter({
      ...filterCopy,
      [fieldName]: value,
    });
    setFilterCopy({
      ...filterCopy,
      [fieldName]: value,
    });
  };

  const onUpdateFieldCopy: SafeParameterSetter<TerminListAdminFilter> = (fieldName, value): void => {
    setFilterCopy(prevState => ({
      ...prevState,
      [fieldName]: value,
    }));
  };

  const onRecordsPerPageChange = (value: number): void => {
    const updatedSearchParams = new URLSearchParams(searchParams.toString());
    updatedSearchParams.set('perPage', value.toString());
    setSearchParams(updatedSearchParams.toString());
  };

  useEffect(() => {
    if (Array.from(searchParams.values()).length === 0
      && JSON.stringify(filter) !== JSON.stringify(emptyFilter)) {
      setFilter(emptyFilter);
      setFilterCopy(emptyFilter);
      setFirstLoad(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  useEffect(() => {
    if (!firstLoad) {
      const urlParamsFromFilter = getUpdatedSearchParams(1, perPageValue, sort, filter);
      setSearchParams(urlParamsFromFilter);
    }
    setFirstLoad(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(filter)]);

  const {
    terminPage,
    terminListStatus,
    pageNumber,
    reloadTerminList: reloadTerminPage,
  } = useTerminListPage(filter, page, perPageValue);

  const handleCloseModal = ():void => {
    setIsOpenModal(false);
    window.parent.postMessage({
      type: 'iframe-to-parent-filter-modal-close',
    }, '*');
    setFilter(filterCopy);
  };

  const handleOpenModal = ():void => {
    setIsOpenModal(true);
    window.parent.postMessage({
      type: 'iframe-to-parent-filter-modal-open',
    }, '*');
    setFilter(filterCopy);
  };

  const clearFilters = (): void => {
    setFilter(emptyFilter);
    setFilterCopy(emptyFilter);
  };

  // tlacidlo na vymazanie filtra sa podla zadania zobrazuje az pri dvoch a viac kriteriach
  const onClearFilters = countActiveFilters(filter) >= 2 ? clearFilters : null;

  return (
    <>
      <TerminListActions filter={filter} isDisabled={terminPage?.totalElements === 0} />
      {isSmallScreen && (
      <SearchInput
        filterCopy={filterCopy}
        label="Hľadaný termín"
        filterSearchText={filterCopy?.hladanyTermin}
        fieldName="hladanyTermin"
        onUpdateFieldCopy={onUpdateFieldCopy}
        setFilter={setFilter}
        className="slovlex-idsk-side-panel-input-wrapper termin-container"
        maxLength={764}
      />
      )}
      <MobileFilterModal
        handleClose={handleCloseModal}
        handleOpen={handleOpenModal}
        isOpen={isOpenModal}
        numberOfResults={terminPage?.totalElements || 0}
        filters={(
          <MobileFilters
            filter={filter}
            onUpdateField={onUpdateField}
          />
          )}
        perPageDataArray={perPageDataArray}
        onPerPageChange={onRecordsPerPageChange}
        perPageValue={perPageValue}
        clearFilters={JSON.stringify(filter) !== JSON.stringify(emptyFilter)
          ? () => {
            setFilter(emptyFilter);
            setFilterCopy(emptyFilter);
          } : null}
        filterTags={(
          <FilterTagsComponent
            filter={filter}
            onUpdateField={onUpdateField}
          />
          )}
      />
      {!isOpenModal && (
      <Row className="legislativny-material-list-component__results-row">
        {!isSmallScreen && (
        <Col className="side-panel-column" width="one-quarter">
          <SidePanel
            setFilter={setFilter}
            filterCopy={filterCopy}
            onUpdateFieldCopy={onUpdateFieldCopy}
            filter={filter}
            onUpdateField={onUpdateField}
          />
        </Col>
        )}
        <Col width="three-quarters">
          <FilterTagsComponent
            filter={filter}
            onUpdateField={onUpdateField}
            onClearFilters={onClearFilters}
          />
          <Loader loading={terminListStatus.loading}>
            <TerminListTable
              terminPage={terminPage}
              pageNumberFromUrl={pageNumber}
              reloadTerminPage={reloadTerminPage}
              isFilterEmpty={isFilterEmpty(filter)}
              onRecordsPerPageChange={onRecordsPerPageChange}
            />
          </Loader>
        </Col>
      </Row>
      )}
    </>
  );
};

function isFilterEmpty(filter: TerminListAdminFilter): boolean {
  return isStringEmpty(filter.hladanyTermin)
    && isBooleanEmpty(filter.publikovany)
    && isPouzivatelEmpty(filter.autorPoslednejZmeny);
}

function isPouzivatelEmpty(pouzivatel: Pouzivatel): boolean {
  return pouzivatel === undefined
    || (isStringEmpty(pouzivatel.nazov)
    && isStringEmpty(pouzivatel.uuid));
}

function isStringEmpty(input: string): boolean {
  return input === undefined || input === '';
}

function isBooleanEmpty(input: boolean): boolean {
  return input === undefined;
}

export default TerminList;
